import { apiFetch, AI_ANALYSIS_TIMEOUT_MS } from './client';
import type { GeoAnalysisResult } from './geo-analysis';

/** Payload espelho do PATCH /viability-costs e coluna custos_viabilidade no banco */
export interface CustosViabilidade {
  /** Percentual de ROI mínimo sobre investimento (ex.: 12 = 12%). No inverso do lance máximo a partir do mercado, omitir equivale a 0%. */
  roiMinimoAceitavelPercent?: number;
  caracteristicas?: string;
  numeroLote?: string;
  tipoLeilao?: string;
  formasPgto?: string;
  dataLeilaoPrimeira?: string;
  valorImovelPrimeiraPraca?: number;
  dataLeilaoSegunda?: string;
  valorImovelSegundaPraca?: number;
  valorMensalCondominio?: number;
  precoMedioMercadoSimilar?: number;
  valorLance?: number;
  dividaIptu?: number;
  dividaCondominio?: number;
  outrosDebitos?: number;
  /** Custos estimados com advogado e/ou desocupação em R$. */
  valorAdvogado?: number;
  /** Custas judiciais: tipicamente sobre o valor de arrematação (padrão 2,5%). */
  custasJudiciaisPercent?: number;
  custasJudiciaisValor?: number;
  comissaoLeiloeiroPercent?: number;
  comissaoLeiloeiroValor?: number;
  /** % sobre valor de arrematação/lance cenário → R$. */
  custoCorretorPercent?: number;
  custoCorretorValor?: number;
  /** IRPF estimado: % aplicado sobre max(0, preço médio mercado − valor da arrematação/lance). */
  custoIrpfPercent?: number;
  custoIrpfValor?: number;
  /** Reparos / reformas: % sobre o lance cenário → valor em R$. */
  custosReparosReformasPercent?: number;
  custosReparosReformas?: number;
  custosCartorio?: number;
  custosCondominio12Meses?: number;
  iptu12Meses?: number;
  /** % sobre o lance máximo (ROI) quando calculável; senão usa o lance cenário para o valor em R$ 12m. */
  custosOportunidadePercent?: number;
  custosOportunidade12Meses?: number;
  custosAquisicao?: number;
  observacoesViabilidade?: string;
}

export interface BeAuction {
  id: string;
  externalId: string;
  sourceId: string;
  sourceName: string;
  tipoImovel: string;
  endereco: string;
  cidade: string;
  estado: string;
  cep?: string;
  bairro?: string;
  lanceMin: number;
  valorMercado?: number;
  valorLeilao2?: number;
  desconto?: number;
  score?: number;
  tier?: string;
  risk?: string;
  roiEstimado?: number;
  dataLeilao?: string;
  dataLeilao2?: string;
  diasRestantes?: number;
  editalUrl?: string;
  detailUrl?: string;
  // Campos enriquecidos via detalhe da Caixa
  areaTotal?: number;
  areaPrivativa?: number;
  areaTerrano?: number;
  matricula?: string;
  comarca?: string;
  inscricaoImob?: string;
  editalNumero?: string;
  leiloeiro?: string;
  modalidade?: string;
  tipoVenda?: string;
  aceitaFgts?: boolean;
  formasPagamento?: string;
  situacaoJuridica?: string;
  imageUrl?: string;
  descricao?: string;
  editalDocUrl?: string;
  matriculaUrl?: string;
  matriculaUrl2?: string;
  /** Número/aviso do lote (prioridade scraping; pode ser preenchido via merge da IA). */
  numeroLote?: string | null;
  detalhesCarregados: boolean;
  detalhesCarregadosEm?: string;
  removidoDoCatalogoEm?: string | null;
  firstDiscoveredVia?: string | null;
  listingConflict?: string | null;
  alerts?: Array<{ code?: string; severity?: string; message?: string }> | null;
  analiseIa?: {
    items: Array<{
      id: string;
      label: string;
      status: 'ok' | 'alerta' | 'critico';
      detalhe: string;
    }>;
    resumo: string;
    geradoEm: string;
    captura_extrada?: {
      ocupacao_classificacao?: 'ocupado' | 'desocupado' | 'nao_conclusivo';
      ocupacao_do_cadastro?: string;
      ocupacao_nos_pdfs?: string;
      onus_desocupacao_arrematante?: boolean | null;
      visitacao?: string;
      notas?: string;
      consolidacao_propriedade?: {
        evidencia?: 'sim' | 'nao' | 'inconclusivo';
        detalhe?: string;
        atos_citados?: string;
      };
    };
    validacaoMercadoAnuncios?: {
      limitacao?: string;
      portaisSugeridos?: string;
      criteriosBusca?: string;
      compatibilidadeAvaliacaoMercado?:
        | 'compativel'
        | 'acima_tipico'
        | 'abaixo_tipico'
        | 'inconclusivo';
      justificativa?: string;
      valorReferencialMercadoRs?: number | null;
      comparaveisConsultados?: Array<{
        portal: string;
        url: string;
        precoRs: number;
        areaM2: number;
        precoM2Rs: number;
        distanciaM?: number | null;
        dentroDoRaio?: boolean;
      }>;
    };
    processosEncontrados?: Array<{
      protocolo?: string;
      comarca?: string;
      tribunal?: string;
    }>;
    scoreRiscoGeral?: {
      nivel?: 'baixo' | 'medio' | 'alto' | 'critico';
      pontuacao?: number;
      motivos?: string[];
    };
  } | null;
  analiseIaEm?: string | null;
  /** Análise de vizinhança persistida no imóvel (Google + OSM). */
  analiseVizinhanca?: GeoAnalysisResult | null;
  analiseVizinhancaEm?: string | null;
  custosViabilidade?: CustosViabilidade | null;
  custosViabilidadeEm?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  geocodedAt?: string | null;
  geocodeStatus?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AuctionMapPin {
  id: string;
  latitude: number;
  longitude: number;
  lanceMin: number;
  tier: string | null;
  cidade: string;
  estado?: string | null;
  endereco: string;
  tipoImovel: string;
  desconto: number | null;
  valorMercado?: number | null;
  valorLeilao2?: number | null;
  roiEstimado?: number | null;
  dataLeilao?: string | null;
  risk?: string | null;
  tipoVenda?: string | null;
  imageUrl?: string | null;
  score?: number | null;
  /** @deprecated use tipoImovel */
  tipo?: string;
}

export interface AuctionMapResponse {
  data: AuctionMapPin[];
}

export interface AuctionMapQueryParams extends Omit<
  AuctionQueryParams,
  'page' | 'sortBy' | 'sortOrder'
> {
  south: number;
  north: number;
  west: number;
  east: number;
  limit?: number;
}

export interface AuctionListResponse {
  data: BeAuction[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface AuctionStats {
  total: number;
  recentCount: number;
  byTier: Array<{ tier: string | null; _count: { id: number } }>;
  /** Agregação por UF — o BE envia `stateCode` (Prisma); `estado` pode existir em respostas antigas. */
  byState: Array<{
    stateCode?: string | null;
    estado?: string | null;
    _count: { id: number };
    _avg: { score: number | null };
  }>;
  byStateTier: Array<{
    stateCode?: string | null;
    estado?: string | null;
    tier: string | null;
    _count: { id: number };
  }>;
  byType: Array<{
    tipoImovel: string;
    _count: { id: number };
    _avg: { roiEstimado: number | null; estimatedRoi?: number | null };
  }>;
  byWeek: Array<{ week: string; total: number }>;
  averages: { desconto: number; roiEstimado: number; lanceMin: number };
  sums: { valorMercado: number; lanceMin: number };
  altoRisco: number;
  altoRiscoPct: number;
  topAuction: {
    id: string;
    score: number;
    tipoImovel: string;
    cidade: string;
    estado: string;
    tier: string | null;
  } | null;
  /** Oportunidade com maior `discountPercent` no catálogo (para KPI do dashboard). */
  maxDiscountAuction: {
    id: string;
    desconto: number;
    tipoImovel: string;
    cidade: string;
    estado: string;
    tier: string | null;
  } | null;
}

/** Chave UF a partir de uma linha de stats (API alinhada ao Prisma: `stateCode`). */
export function stateCodeFromStatsRow(row: {
  stateCode?: string | null;
  estado?: string | null;
}): string {
  return String(row.stateCode ?? row.estado ?? '')
    .trim()
    .toUpperCase();
}

/** Mapa UF → quantidade (para o filtro de estado). Normaliza chaves em maiúsculas. */
export function countsByEstadoFromStats(byState: AuctionStats['byState']): Record<string, number> {
  const out: Record<string, number> = {};
  for (const row of byState) {
    const k = stateCodeFromStatsRow(row);
    if (!k) continue;
    out[k] = (out[k] ?? 0) + row._count.id;
  }
  return out;
}

/** Linha de GET /opportunities/stats/cities — BE usa `city` / `stateCode` (Prisma). */
export interface AuctionCityAggRow {
  city?: string | null;
  cidade?: string | null;
  stateCode?: string | null;
  estado?: string | null;
  count: number;
}

/** GET /opportunities/stats/cities — filtro UI de cidade com contagens */
export interface AuctionCityAgg {
  scope: 'uf' | 'br';
  totalMatching: number;
  items: AuctionCityAggRow[];
}

export function cityAggItemName(it: AuctionCityAggRow): string {
  return String(it.city ?? it.cidade ?? '').trim();
}

export function cityAggItemStateCode(it: AuctionCityAggRow): string | null {
  const s = String(it.stateCode ?? it.estado ?? '')
    .trim()
    .toUpperCase();
  return s.length ? s : null;
}

/** Linha de GET /opportunities/stats/districts — BE usa `district`. */
export interface AuctionBairroAggRow {
  district?: string | null;
  bairro?: string | null;
  count: number;
}

/** GET /opportunities/stats/districts — filtro UI de bairro (district) com contagens */
export interface AuctionBairroAgg {
  totalMatching: number;
  items: AuctionBairroAggRow[];
}

export function bairroAggItemName(it: AuctionBairroAggRow): string {
  return String(it.district ?? it.bairro ?? '').trim();
}

/** GET /opportunities/stats/vendors */
export interface AuctionVendorStats {
  total: number;
  vendors: Record<string, number>;
}

/** Ordenação aceita por GET /opportunities (inglês, alinhado ao Prisma). */
export type AuctionSortByApi = 'score' | 'minBid' | 'discountPercent' | 'createdAt';

/** UI/URL legados (`desconto`, `lanceMin`) → query string do BE. */
export function toAuctionSortByApi(raw: string): AuctionSortByApi {
  switch (raw) {
    case 'lanceMin':
      return 'minBid';
    case 'desconto':
      return 'discountPercent';
    case 'score':
    case 'createdAt':
      return raw;
    default:
      return 'discountPercent';
  }
}

export interface AuctionQueryParams {
  estado?: string;
  cidade?: string;
  bairro?: string;
  tier?: string;
  tipo?: string;
  q?: string;
  risk?: string;
  tipoVenda?: string;
  lanceMinFrom?: number;
  lanceMinTo?: number;
  roiMin?: number;
  roiMax?: number;
  scoreMin?: number;
  page?: number;
  limit?: number;
  sortBy?: AuctionSortByApi;
  sortOrder?: 'asc' | 'desc';
  /** Filtro no BE: `createdAt` >= agora − N dias (ex.: 2 para últimos 2 dias). */
  createdWithinDays?: number;
  /** Fornecedor (`sourceId` ou `bradesco_vitrine` → vitrine). */
  vendor?: string;
}

function toQuery(params: object): string {
  const q = new URLSearchParams();
  Object.entries(params as Record<string, string | number | boolean | undefined | null>).forEach(
    ([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    }
  );
  const str = q.toString();
  return str ? `?${str}` : '';
}

export interface EnrichmentStats {
  total: number;
  enriched: number;
  pending: number;
  pct: number;
  /** true quando o BE está drenando a fila Caixa em segundo plano (2ª fase) */
  caixaBackgroundEnrichment?: boolean;
}

/** Resposta de POST /opportunities/onr-cartorio-calculo */
export interface OnrCartorioCalculoResponse {
  fonteUrl: string;
  totalOnr: number;
  valorImovel: string;
  uf: string;
  codigoMunicipio: number;
  consultaId: number;
  itens: Array<{ descricao: string; subtotal: number }>;
  observacaoTotal?: string;
  mensagemExtras?: unknown;
  extraInformacao?: unknown;
}

export type AuctionDocumentAnalysis = NonNullable<BeAuction['analiseIa']>;

export type AiAnalysisJobStatus = 'PENDING' | 'RUNNING' | 'SUCCEEDED' | 'FAILED';

export type AiDocumentAnalysisJob = {
  jobId: string;
  opportunityId: string;
  status: AiAnalysisJobStatus;
  error?: string | null;
  result?: AuctionDocumentAnalysis | null;
  createdAt: string;
  startedAt?: string | null;
  finishedAt?: string | null;
};

export interface GeocodeQueueMissingResult {
  enqueued: number;
}

export interface GeocodeReprocessResult {
  clearedFailed: number;
  clearedMunicipality: number;
  clearedLegacyOk: number;
  enqueued: number;
}

export const opportunitiesApi = {
  list: (params: AuctionQueryParams = {}) =>
    apiFetch<AuctionListResponse>(`/opportunities${toQuery(params)}`),

  getById: (id: string) => apiFetch<BeAuction>(`/opportunities/${id}`),

  getStats: () => apiFetch<AuctionStats>('/opportunities/stats'),

  getVendorStats: () => apiFetch<AuctionVendorStats>('/opportunities/stats/vendors'),

  getCityStats: (params?: { estado?: string }) => {
    const est = params?.estado?.trim();
    const qs = est && est.length > 0 ? `?stateCode=${encodeURIComponent(est)}` : '';
    return apiFetch<AuctionCityAgg>(`/opportunities/stats/cities${qs}`);
  },

  getDistrictStats: (params: AuctionQueryParams & { cidades: string[] }) => {
    const serialized = params.cidades?.length
      ? params.cidades
          .map((v) => {
            const sep = v.indexOf('\u001e');
            if (sep >= 0) {
              return `${v.slice(0, sep)}|${v.slice(sep + 1)}`;
            }
            return v;
          })
          .join(',')
      : '';
    if (!serialized) return Promise.resolve({ totalMatching: 0, items: [] });
    const {
      cidades: _cidades,
      cidade: _cidade,
      bairro: _b,
      page: _p,
      limit: _l,
      sortBy: _sb,
      sortOrder: _so,
      ...listRest
    } = params;
    const q = new URLSearchParams();
    q.set('city', serialized);
    const uf = params.estado?.trim();
    if (uf) q.set('stateCode', uf);
    Object.entries(listRest).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    return apiFetch<AuctionBairroAgg>(`/opportunities/stats/districts?${q.toString()}`);
  },

  mapPins: (params: AuctionMapQueryParams) =>
    apiFetch<AuctionMapResponse>(`/opportunities/map${toQuery(params)}`),

  /** Enfileira geocodificação (Nominatim) para leilões ainda sem lat/lng — até `limit` por chamada. */
  queueGeocodeMissing: (limit = 200) => {
    const n = Math.min(500, Math.max(1, limit));
    return apiFetch<GeocodeQueueMissingResult>(`/opportunities/geocode/queue-missing?limit=${n}`, {
      method: 'POST',
      skipAuth: true,
    });
  },

  /**
   * Opcionalmente reseta tentativas (`scopes`) e depois enfileira até `limit` sem coordenadas.
   * Scopes: `failed`, `municipality`, `legacy_ok`.
   */
  reprocessGeocode: (opts: { limit?: number; scopes: string[] }) => {
    const n = Math.min(500, Math.max(1, opts.limit ?? 200));
    const scopes =
      opts.scopes.length > 0 ? opts.scopes.map((s) => s.trim().toLowerCase()).join(',') : 'failed';
    return apiFetch<GeocodeReprocessResult>(
      `/opportunities/geocode/reprocess?limit=${n}&scopes=${encodeURIComponent(scopes)}`,
      { method: 'POST', skipAuth: true }
    );
  },

  /** Detalhe Caixa (noticeUrl) — `async` evita timeout do Nginx em deploy (Playwright demora). */
  enrichCaixaDetail: (id: string, opts?: { force?: boolean; async?: boolean }) => {
    const params = new URLSearchParams();
    if (opts?.force) params.set('force', 'true');
    if (opts?.async) params.set('async', 'true');
    const q = params.toString() ? `?${params}` : '';
    return apiFetch<Record<string, unknown> | { ok: true; accepted: boolean; status: string }>(
      `/scraper/opportunities/${encodeURIComponent(id)}/caixa-enrich${q}`,
      {
        method: 'POST',
      }
    );
  },

  enrichZuk: (id: string) =>
    apiFetch<{ ok: boolean; updated: boolean }>(
      `/scraper/opportunities/${encodeURIComponent(id)}/zuk-enrich`,
      { method: 'POST' }
    ),

  enrichVip: (id: string) =>
    apiFetch<{ ok: boolean; updated: boolean }>(
      `/scraper/opportunities/${encodeURIComponent(id)}/vip-enrich`,
      { method: 'POST' }
    ),

  enrichFreitas: (id: string) =>
    apiFetch<{ ok: boolean; updated: boolean }>(
      `/scraper/opportunities/${encodeURIComponent(id)}/freitas-enrich`,
      { method: 'POST' }
    ),

  enrichSodre: (id: string) =>
    apiFetch<{ ok: boolean; updated: boolean }>(
      `/scraper/opportunities/${encodeURIComponent(id)}/sodre-enrich`,
      { method: 'POST' }
    ),

  enrichBatch: (limit = 50, delayMs = 800) =>
    apiFetch<{ processed: number; enriched: number; failed: number; remaining: number }>(
      `/opportunities/enrich/batch?limit=${limit}&delay=${delayMs}`,
      { method: 'POST' }
    ),

  getEnrichmentStats: () => apiFetch<EnrichmentStats>('/opportunities/enrich/stats'),

  // Análise IA assíncrona: POST inicia job (202); GET faz polling do status/resultado.
  startAnalyzeDocuments: (id: string) =>
    apiFetch<AiDocumentAnalysisJob>(`/opportunities/${id}/analyze-documents`, {
      method: 'POST',
      // Resposta imediata — não precisa do timeout longo.
      timeoutMs: 30_000,
    }),

  getAnalyzeDocumentsJob: (jobId: string) =>
    apiFetch<AiDocumentAnalysisJob>(
      `/opportunities/analyze-documents/jobs/${encodeURIComponent(jobId)}`,
      { timeoutMs: 20_000 }
    ),

  /** @deprecated Preferir startAnalyzeDocuments + polling via useAiDocumentAnalysis */
  analyzeDocuments: async (id: string) => {
    const started = await opportunitiesApi.startAnalyzeDocuments(id);
    const deadline = Date.now() + AI_ANALYSIS_TIMEOUT_MS;
    let job = started;
    while (job.status === 'PENDING' || job.status === 'RUNNING') {
      if (Date.now() > deadline) {
        throw new Error(
          `A análise demorou mais de ${Math.round(AI_ANALYSIS_TIMEOUT_MS / 1000)}s. Tente de novo em instantes.`
        );
      }
      await new Promise((r) => setTimeout(r, 2000));
      job = await opportunitiesApi.getAnalyzeDocumentsJob(job.jobId);
    }
    if (job.status === 'FAILED') {
      throw new Error(job.error?.trim() || 'Erro ao analisar documentos.');
    }
    if (!job.result) {
      throw new Error('Análise concluída sem resultado.');
    }
    return job.result as AuctionDocumentAnalysis;
  },

  analyzeNeighborhood: (id: string, body?: { radius?: number }) =>
    apiFetch<GeoAnalysisResult>(`/opportunities/${id}/neighborhood-analysis`, {
      method: 'POST',
      body: body ?? { radius: 800 },
      timeoutMs: AI_ANALYSIS_TIMEOUT_MS,
    }),

  saveViabilitySheet: (id: string, body: CustosViabilidade) =>
    apiFetch<BeAuction>(`/opportunities/${id}/viability-sheet`, {
      method: 'PATCH',
      body,
    }),

  calculadoraOnrCartorio: (body: {
    uf: string;
    valorImovel: number;
    cidade?: string;
    codigoMunicipio?: number;
    consultaId?: number;
  }) =>
    apiFetch<OnrCartorioCalculoResponse>('/opportunities/onr-cartorio-calculo', {
      method: 'POST',
      body,
    }),
};
